/**
 * Application Root Routes
 *
 * ─── Access control layers ───────────────────────────────────────
 *
 *  1. authGuard                 — user must be authenticated (JWT valid)
 *  2. carrierGuard              — user must have ofertaLance = true
 *  3. shipperGuard              — user must have ofertaFrete = true
 *  4. authenticatedProfileGuard — carrier OR shipper (shared features)
 *
 * ─── Route groups ────────────────────────────────────────────────
 *
 *  PUBLIC   /                           → login, register, forgot-password…
 *  PRIVATE  /app                        → viewRedirectGuard (smart redirect)
 *           /app/carriers               → carrierGuard
 *           /app/shipper                → shipperGuard
 *           /app/configurations-carrier → carrierGuard
 *           /app/configurations-shipper → shipperGuard
 *           /app/portal                 → carrierGuard  (carrier-only feature)
 *           /app/payments               → authenticatedProfileGuard
 *           /app/iaassist               → shipperGuard
 *
 * ─── Adding a new route ──────────────────────────────────────────
 *
 *  1. Decide access: carrier-only | shipper-only | shared?
 *  2. Apply canActivate: [<matching guard>] on the parent route.
 *  3. Create a routes file under features/<scope>/<feature>.routes.ts
 *  4. Add a breadcrumb key to route data and to all 3 locale files.
 *  5. Add the menu entry to private-layout.component.ts if sidebar-visible.
 */

import { Routes } from '@angular/router';
import { PublicLayoutComponent } from './layout/public/public-layout.component';
import { PrivateLayoutComponent } from './layout/private/private-layout.component';
import {
  authGuard,
  carrierGuard,
  shipperGuard,
  authenticatedProfileGuard,
  viewRedirectGuard,
  shipperUserTypeGuard
} from './core/guards';

export const routes: Routes = [

  // ═══════════════════════════════════════════════════════════════
  // PUBLIC — No authentication required
  // ═══════════════════════════════════════════════════════════════
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full'
      },
      {
        path: '',
        loadChildren: () =>
          import('./features/public/public.routes').then(m => m.PUBLIC_ROUTES)
      }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // PRIVATE — Requires authentication (authGuard)
  // Each child also requires a business-profile guard (see each route).
  // ═══════════════════════════════════════════════════════════════
  {
    path: 'app',
    component: PrivateLayoutComponent,
    canActivate: [authGuard],
    children: [

      // ── Smart redirect: /app → carrier or shipper home ──────────
      // viewRedirectGuard returns a UrlTree synchronously, so the
      // component below is never instantiated.
      {
        path: '',
        pathMatch: 'full',
        canActivate: [viewRedirectGuard],
        loadComponent: () =>
          import('./shared/components/under-construction/under-construction.component').then(
            m => m.UnderConstructionComponent
          )
      },

      // ── CARRIER — requires ofertaLance = true ───────────────────
      {
        path: 'carriers',
        canActivate: [carrierGuard],
        data: { breadcrumb: 'private.breadcrumb.home' },
        loadChildren: () =>
          import('./features/carrier/carrier.routes').then(m => m.CARRIER_ROUTES)
      },

      // ── SHIPPER — requires ofertaFrete = true ───────────────────
      {
        path: 'shipper',
        canActivate: [shipperGuard],
        canActivateChild: [shipperUserTypeGuard],
        data: { breadcrumb: 'private.breadcrumb.home' },
        loadChildren: () =>
          import('./features/shipper/shipper.routes').then(m => m.SHIPPER_ROUTES)
      },

      // ── CARRIER SETTINGS — requires ofertaLance = true ─────────
      {
        path: 'configurations-carrier',
        canActivate: [carrierGuard],
        data: { breadcrumb: 'private.breadcrumb.settings' },
        loadChildren: () =>
          import('./features/carrier/settings/configurations-carrier.routes').then(
            m => m.CONFIGURATIONS_CARRIER_ROUTES
          )
      },

      // ── SHIPPER SETTINGS — requires ofertaFrete = true ─────────
      {
        path: 'configurations-shipper',
        canActivate: [shipperGuard],
        canActivateChild: [shipperUserTypeGuard],
        data: { breadcrumb: 'private.breadcrumb.settings' },
        loadChildren: () =>
          import('./features/shipper/settings/configurations-shipper.routes').then(
            m => m.CONFIGURATIONS_SHIPPER_ROUTES
          )
      },

      // ── CARRIER PORTAL — requires ofertaLance = true ───────────
      // Portal is a carrier-only feature (CT-e management).
      {
        path: 'portal',
        canActivate: [carrierGuard],
        data: { breadcrumb: 'private.breadcrumb.portal' },
        loadChildren: () =>
          import('./features/shared/portal/portal.routes').then(m => m.PORTAL_ROUTES)
      },

      // ── PAYMENTS — carrier + shipper ────────────────────────────
      {
        path: 'payments',
        canActivate: [authenticatedProfileGuard],
        data: { breadcrumb: 'private.breadcrumb.invoice_extract' },
        loadChildren: () =>
          import('./features/shared/payments/payments.routes').then(m => m.PAYMENTS_ROUTES)
      },

      // ── SARA / IA ASSIST — shipper-only ─────────────────────────
      {
        path: 'iaassist',
        canActivate: [shipperGuard],
        data: { breadcrumb: 'private.breadcrumb.iaassist' },
        loadChildren: () =>
          import('./features/shared/iaassist/iaassist.routes').then(m => m.IAASSIST_ROUTES)
      },

      // ── POLÍTICAS — carrier + shipper, apenas autenticado ───────
      // Referência ANTT: metodologia e transparência. Linkada no rodapé da sidebar
      // privada (private-sidebar.component) e no card ANTT da oferta spot. Só
      // acessível dentro da plataforma — não é uma página pública.
      {
        path: 'politicas',
        loadComponent: () =>
          import('./features/shared/pages/policies/policies.component').then(
            m => m.PoliciesComponent
          )
      }
    ]
  },

  // ─── Access denied page ───
  {
    path: 'access-denied',
    loadComponent: () =>
      import('./features/public/pages/access-denied/access-denied.component').then(
        m => m.AccessDeniedComponent
      )
  },

  // ─── "Collection Order" print page — public, unauthenticated, no layout ───
  // Reached via the externally-shared link on a contract offer's collect or a
  // SPOT offer's winning bid ("Ordem de coleta" / "Rastreamento" / linkExterno).
  // The `external-link` prefix makes it visually obvious (even before the page
  // loads) that this isn't a logged-in app page. `offerType` ('contract' | 'spot')
  // picks which backend endpoint and field layout the page uses. `clientId`/
  // `offerId` are masked with maskRouteId(), matching this project's convention
  // of never exposing raw sequential IDs in a URL.
  {
    path: 'external-link/collection-order/:offerType/:clientId/:offerId/:hash',
    loadComponent: () =>
      import('./features/public/pages/collection-order/collection-order.component').then(
        m => m.CollectionOrderComponent
      )
  },

  // ─── Wildcard redirect ───
  {
    path: '**',
    redirectTo: 'login'
  }
];
